import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import React, { useMemo } from "react";
import { MERGE_LEAD } from "../../../../graphql/mutation";
import { GET_POTENTIAL_LEADS_BY_ID } from "../../../../graphql/query";
import useLang from "../../../../hooks/useLang";
import MergeRecordsDrawer from "./MergeRecordsDrawer";
import { LEAD_MERGE_FIELDS, buildLeadMergeVariables } from "./mergeLeadFields";

/**
 * Merge leads (EAI-1816).
 *
 * Opened from the "Duplicate record found for this lead." banner on a lead card
 * in the Activity Center list. Supplies the lead-specific parts — the
 * duplicates query, the field config and the create mutation — to the shared
 * ``MergeRecordsDrawer``, which owns the comparison UI.
 *
 * The merge is entirely server-side, via ``mergeLead`` (EAI-1871). The record
 * the user made master SURVIVES and is updated in place — it is no longer
 * archived into a newly created lead the way ``createLead(potentialLeads:)``
 * did. That matters because a lead id is referenced by CRM records, phone and
 * email routing, conversation threads and campaign enrolments, all of which the
 * old create-path merge invalidated.
 *
 * Each merged-away source is archived (``is_deleted = True``, never a hard
 * delete) and its related rows — phones, emails, addresses, notes, tasks,
 * messages, activity history — are re-parented onto the master. So this only
 * decides *what* to send.
 */

/** The design auto-dismisses the success toast after 3.5s. */
const TOAST_DURATION_SECONDS = 3.5;

const MergeLeadsDrawer = ({ open, onClose, lead, onMerged }) => {
  const [lang] = useLang();

  const duplicateIds = useMemo(
    () => (lead?.potentialLeads || []).map(Number).filter(Boolean),
    [lead]
  );

  /**
   * Fetch the lead the banner sits on ALONGSIDE its duplicates, rather than
   * reusing the list row for the first column. The list query is leaner than
   * this one (no addresses/emails), so mixing the two shapes would leave the
   * master column showing N/A for fields the record actually has.
   */
  const leadIds = useMemo(
    () => (lead?.id ? [+lead.id, ...duplicateIds] : []),
    [lead, duplicateIds]
  );

  const { data, loading, error } = useQuery(GET_POTENTIAL_LEADS_BY_ID, {
    variables: { leadIds },
    skip: !open || leadIds.length < 2,
    // Sources are archived by the merge, so never serve them from cache.
    fetchPolicy: "network-only",
  });

  /**
   * The banner's lead first, then the duplicates — matching the column order
   * the design shows. Already-merged records are dropped so a stale
   * ``potentialLeads`` entry can't offer a deleted lead as mergeable.
   */
  const records = useMemo(() => {
    const fetched = (data?.getLeadsById || []).filter(item => !item?.isDeleted);
    const current = fetched.find(item => +item?.id === +lead?.id);
    if (!current) return [];
    return [current, ...fetched.filter(item => +item?.id !== +lead?.id)];
  }, [data, lead]);

  const [mergeLead, { loading: merging }] = useMutation(MERGE_LEAD, {
    onError: err => message.error(err?.message || lang.somethingWentWrong),
  });

  const handleMerge = async ({ primaryIdx, excluded, picks, includedCount }) => {
    const res = await mergeLead({
      variables: buildLeadMergeVariables({ records, primaryIdx, excluded, picks }),
    });
    if (res?.data?.mergeLead?.statusCode === 200) {
      onClose?.();
      message.success(
        lang.mergeLeadsSuccessToast.replace("{{count}}", includedCount),
        TOAST_DURATION_SECONDS
      );
      // Refresh so the master shows its merged-in values and the archived
      // sources stop being offered as duplicates.
      onMerged?.();
    } else {
      message.error(res?.data?.mergeLead?.message || lang.somethingWentWrong);
    }
  };

  return (
    <MergeRecordsDrawer
      open={open}
      onClose={onClose}
      title={lang.mergeLeadsTitle}
      infoBody={lang.mergeLeadsInfoPanelBody}
      confirmTitle={lang.mergeLeadsConfirmTitle}
      confirmBody={lang.mergeLeadsConfirmBody}
      emptyMessage={lang.mergeLeadsNoDuplicatesFound}
      records={records}
      fields={LEAD_MERGE_FIELDS}
      createdKey="leadCreatedDate"
      loading={loading}
      error={error}
      merging={merging}
      onConfirm={handleMerge}
    />
  );
};

export default MergeLeadsDrawer;
